import { PROJECTOR } from '@/constants/object';
import * as THREE from 'three';

/**
 * Compute projector rotation (pitch/yaw in degrees) from position and target.
 * Uses direct trigonometry from the direction vector to avoid gimbal lock
 * that occurs with matrix-based Euler extraction near Y rotation ±90°.
 *
 * For XYZ Euler order with roll=0, the forward direction (-Z) after rotation is:
 *   forward = (-sin(yaw)*cos(pitch), sin(pitch), -cos(yaw)*cos(pitch))
 * Solving for pitch and yaw:
 *   pitch = asin(dir.y)
 *   yaw   = atan2(-dir.x, -dir.z)
 *
 * Roll (rotation.z) is preserved from existingRotationZ if provided.
 */
export function computeRotationFromPositionTarget(pos, tgt, existingRotationZ = 0) {
  const dx = (tgt.x ?? 0) - (pos.x ?? 0);
  const dy = (tgt.y ?? 0) - (pos.y ?? 0);
  const dz = (tgt.z ?? 0) - (pos.z ?? 0);
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 0.000001) return null;

  const nx = dx / len;
  const ny = dy / len;
  const nz = dz / len;

  const pitch = Math.asin(THREE.MathUtils.clamp(ny, -1, 1));
  const yaw = Math.atan2(-nx, -nz);

  return {
    x: pitch * (180 / Math.PI),
    y: yaw * (180 / Math.PI),
    z: existingRotationZ,
  };
}

/**
 * Build a quaternion that orients local -Z toward `forward`, using a makeBasis
 * orthonormal frame. Handles the degenerate case where forward is parallel to up.
 *
 * @param {THREE.Vector3} forward - Normalized direction to look at
 * @param {Object} [options]
 * @param {THREE.Vector3} [options.up=Vector3(0,1,0)] - Up hint for computing the basis
 * @param {number} [options.roll=0] - Roll in radians around local Z after orientation
 * @param {THREE.Vector3} [options.degenerateRight] - Right vector to use when forward ≈ up.
 *   If omitted, falls back to (1,0,0).
 * @returns {{ quaternion: THREE.Quaternion, up: THREE.Vector3 }}
 */
export function buildLookAtQuaternion(forward, { up, roll = 0, degenerateRight } = {}) {
  const localZ = forward.clone().negate();
  const upHint = up || new THREE.Vector3(0, 1, 0);

  const localX = new THREE.Vector3().crossVectors(upHint, localZ);

  if (localX.lengthSq() < 0.000001) {
    // Forward is (nearly) parallel to up — use fallback right vector
    if (degenerateRight) {
      localX.copy(degenerateRight);
    } else {
      localX.set(1, 0, 0);
    }
  }
  localX.normalize();

  const localY = new THREE.Vector3().crossVectors(localZ, localX).normalize();

  const matrix = new THREE.Matrix4().makeBasis(localX, localY, localZ);
  const quaternion = new THREE.Quaternion().setFromRotationMatrix(matrix);

  if (Math.abs(roll) > 0.0001) {
    quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), roll));
  }

  return { quaternion, up: localY };
}

/**
 * Calculate all projector statistics from node data
 * @param {Object} node - The projector node from findNodeById
 * @param {Object} projectorData - Optional projector library data
 * @returns {Object} All calculated projector statistics
 */
export function calculateProjectorStats(node, projectorData = null, userData) {
  if (!node || node.type !== 'projector') {
    return null;
  }

  const nodeData = node.data || {};

  // Get position and target from node data
  const position = new THREE.Vector3(nodeData.position?.x || 0, nodeData.position?.y || 0, nodeData.position?.z || 0);
  const targetPosition = new THREE.Vector3(nodeData.target?.x || 0, nodeData.target?.y || 0, nodeData.target?.z || 0);

  // Calculate distance from position to target
  const distance = Math.max(position.distanceTo(targetPosition), 0.01);

  // Get projector properties (prefer library data for specs, node data for settings)
  const throwRatio = Math.min(
    Math.max(parseFloat(projectorData?.throw_ratio || nodeData.throwRatio || 1.4), PROJECTOR.MIN_THROW_RATIO),
    PROJECTOR.MAX_THROW_RATIO
  );
  const resolutionWidth = projectorData?.resolution_width || nodeData.resolutionWidth || 1920;
  const resolutionHeight = projectorData?.resolution_height || nodeData.resolutionHeight || 1080;
  const lumens = projectorData?.lumens || nodeData.lumens || 20000;
  const brightnessPercent = nodeData.power_brightness || 100;

  // Calculate projected dimensions
  const projectionWidth = distance / throwRatio;
  const aspectRatio = resolutionWidth / resolutionHeight;
  const projectionHeight = projectionWidth / aspectRatio;

  // Calculate pixel size (mm per pixel)
  const pixelSize = (projectionHeight * 1000) / resolutionHeight;

  // Calculate lux at center point
  const effectiveLumens = lumens * (brightnessPercent / 100);

  // Calculate spotlight angle from throw ratio
  // For a given distance, the image width determines the angle
  const actualImageWidth = distance / throwRatio;
  const halfAngle = Math.atan(actualImageWidth / 2 / distance);
  const angle = halfAngle * 2;

  // Calculate luminous intensity (candelas) for a spotlight
  // Formula: I = Φ / (2π × (1 - cos(θ/2)))
  const solidAngle = 2 * Math.PI * (1 - Math.cos(halfAngle));
  const luminousIntensity = effectiveLumens / solidAngle;

  // Calculate illuminance (lux) at center point
  // Formula: E = I / d²
  const luxAtCenter = luminousIntensity / (distance * distance);

  return {
    distance,
    pixelSize,
    throwRatio,
    resolution: `${resolutionWidth}x${resolutionHeight}`,
    resolutionWidth,
    resolutionHeight,
    projectionWidth,
    projectionHeight,
    lumens,
    brightnessPercent,
    angle, // in radians
    spotlightAngle: ((angle * 180) / Math.PI).toFixed(1), // in degrees
    luxAtCenter,
    position,
    targetPosition,
  };
}
